import { createContext } from "preact";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import { buildRoomSocketUrl } from "../../features/rooms/model/lobbyApi";
import { useRoomStore } from "../../features/rooms/model/roomStore";
import { com } from "../../proto/game.pb";
import { useUserStore } from "../../store/userStore";

export type WebSocketStatus =
  | "idle"
  | "bootstrapping"
  | "connecting"
  | "open"
  | "closing"
  | "closed"
  | "error";

export type ChatMode = com.achlid.crash.game.ChatCapabilities.Mode;
const { Mode: ChatModeEnum } = com.achlid.crash.game.ChatCapabilities;
const { MessageType } = com.achlid.crash.game.GameMessage;

interface WebSocketContextValue {
  status: WebSocketStatus;
  socket: WebSocket | null;
  missedWindows: number; // how many 3s windows passed without any server message (0..3)
  /**
   * Монотонная controller/tab-local attempt generation (§11 чат-гайда).
   * Меняется в начале каждой full connection attempt — подписчики обязаны
   * сбрасывать всё socket-derived state при изменении.
   */
  generation: number;
  /** Room активного connection. Не считается неизменяемой между attempts. */
  roomId: number | null;
  /**
   * Capability из последнего `CONNECTED`. Fail closed: до `CONNECTED` и после
   * любого disconnect — `CHAT_MODE_NONE`.
   */
  chatMode: ChatMode;
  send: (data: string | ArrayBufferLike | Blob | ArrayBufferView) => boolean;
  sendProto: (msg: com.achlid.crash.game.IGameMessage) => boolean;
  onMessage: (
    type: number,
    handler: (msg: com.achlid.crash.game.IGameMessage) => void
  ) => () => void;
  close: (code?: number, reason?: string) => void;
  /** Explicit user retry: запускает новую full connection attempt. */
  reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextValue | undefined>(
  undefined
);

// eslint-disable-next-line react-refresh/only-export-components
export function useWebSocket() {
  const ctx = useContext(WebSocketContext);
  if (!ctx)
    throw new Error("useWebSocket must be used within WebSocketProvider");
  return ctx;
}

interface ProviderProps {
  children: preact.ComponentChildren;
}

/** Frontend-owned backoff. Backend не задаёт normative timing (§11). */
const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000];
const MAX_AUTO_ATTEMPTS = BACKOFF_MS.length;

/** Запрос кошелька после исходного снимка состояния комнаты (§10 demo-lobby). */
function sendGetWallet(socket: WebSocket): void {
  if (socket.readyState !== WebSocket.OPEN) return;
  try {
    const bin = com.achlid.crash.game.GameMessage.encode({
      type: MessageType.GET_WALLET,
      getWallet: {},
    }).finish();
    socket.send(bin);
  } catch {
    // Запрос баланса не должен ронять обработку кадра.
  }
}

/**
 * Base для dev-session bootstrap, либо `null`, если bootstrap не нужен.
 *
 * `POST /game/dev-session` — это fixture чат-гайда для общего
 * `dev.achlid.com/game`. В контракте лобби его нет: комнаты живут на
 * отдельных хостах `room-N.dev.achlid.com`, и каждое подключение там создаёт
 * нового игрока (§9 demo-lobby). Безусловный bootstrap ушёл бы на хост
 * комнаты и завалил бы всё соединение, поэтому шаг включается только явным
 * `VITE_CHAT_DEV_BOOTSTRAP=true`.
 */
function resolveBootstrapBase(wsUrl: string | undefined): string | null {
  const enabled = import.meta.env?.VITE_CHAT_DEV_BOOTSTRAP === "true";
  if (!enabled) return null;

  const explicit = import.meta.env?.VITE_CHAT_BOOTSTRAP_BASE as
    | string
    | undefined;
  if (explicit) return explicit.replace(/\/+$/, "");
  if (!wsUrl) return null;
  try {
    const url = new URL(wsUrl);
    const scheme = url.protocol === "ws:" ? "http:" : "https:";
    return `${scheme}//${url.host}`;
  } catch {
    return null;
  }
}

export function WebSocketProvider({ children }: ProviderProps) {
  /**
   * Комната выбирается адресом подключения: `webSocketUrl` записи каталога
   * плюс её же `roomId` (§5 demo-lobby). Пока комната не выбрана, socket не
   * открывается.
   */
  const selectedRoom = useRoomStore((state) => state.selectedRoom);

  const WS_URL = useMemo(
    () => (selectedRoom ? buildRoomSocketUrl(selectedRoom) : undefined),
    [selectedRoom]
  );
  const bootstrapBase = useMemo(() => resolveBootstrapBase(WS_URL), [WS_URL]);

  const [status, setStatus] = useState<WebSocketStatus>("idle");
  const [missedWindows, setMissedWindows] = useState<number>(0);
  const [generation, setGeneration] = useState(0);
  const [roomId, setRoomId] = useState<number | null>(null);
  const [chatMode, setChatMode] = useState<ChatMode>(
    ChatModeEnum.CHAT_MODE_NONE
  );

  const socketRef = useRef<WebSocket | null>(null);
  const lastActivityAtRef = useRef<number>(Date.now());
  const subscribersRef = useRef<
    Map<number, Set<(msg: com.achlid.crash.game.IGameMessage) => void>>
  >(new Map());

  /** Текущая generation, читаемая синхронно из async callbacks. */
  const generationRef = useRef(0);
  /** Отвязка handlers + закрытие socket предыдущей generation. */
  const teardownRef = useRef<(() => void) | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const autoAttemptsRef = useRef(0);
  /** Намеренное закрытие (hidden tab, unmount) не вызывает auto-reconnect. */
  const intentionalCloseRef = useRef(false);
  const startAttemptRef = useRef<(() => void) | null>(null);
  /** `GET_WALLET` отправляется один раз на соединение, после `GAME_STATE`. */
  const walletRequestedRef = useRef(false);

  const notifySubscribers = useCallback(
    (msg: com.achlid.crash.game.IGameMessage) => {
      const subs = subscribersRef.current.get(msg.type as number);
      if (!subs) return;
      subs.forEach((handler) => {
        try {
          handler(msg);
        } catch {
          /* isolate handler errors */
        }
      });
    },
    []
  );

  const decodeGameMessage = useCallback(
    async (
      data: unknown
    ): Promise<com.achlid.crash.game.IGameMessage | null> => {
      const isBlobLike = (val: any): val is Blob =>
        typeof val?.arrayBuffer === "function";
      try {
        if (data instanceof ArrayBuffer) {
          return com.achlid.crash.game.GameMessage.decode(new Uint8Array(data));
        }
        if (isBlobLike(data)) {
          const buf = await (data as Blob).arrayBuffer();
          return com.achlid.crash.game.GameMessage.decode(new Uint8Array(buf));
        }
      } catch {
        // Malformed Protobuf: partial mutation не применяется.
        return null;
      }
      return null;
    },
    []
  );

  const scheduleRetry = useCallback(() => {
    if (intentionalCloseRef.current) return;
    if (autoAttemptsRef.current >= MAX_AUTO_ATTEMPTS) {
      setStatus("error");
      return;
    }
    const delay = BACKOFF_MS[autoAttemptsRef.current];
    autoAttemptsRef.current += 1;
    if (retryTimerRef.current != null)
      window.clearTimeout(retryTimerRef.current);
    retryTimerRef.current = window.setTimeout(() => {
      retryTimerRef.current = null;
      startAttemptRef.current?.();
    }, delay);
  }, []);

  /**
   * Full connection attempt (§11). Порядок обязателен: синхронно выделить
   * новую generation -> retire предыдущую -> сбросить state -> и только затем
   * начать asynchronous bootstrap.
   */
  const startAttempt = useCallback(() => {
    if (!WS_URL) {
      // Комната ещё не выбрана — соединение не начинаем.
      setStatus("idle");
      return;
    }

    // 1-3. Новая generation и retirement предыдущей.
    const attemptGeneration = generationRef.current + 1;
    generationRef.current = attemptGeneration;

    teardownRef.current?.();
    teardownRef.current = null;
    if (retryTimerRef.current != null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    // Комната фиксируется на всю попытку: поздние кадры сверяются с этим
    // значением, а не с текущим выбором в сторе.
    const expectedRoomId = selectedRoom?.roomId ?? null;

    // 4. Reset socket-derived state. Подписчики чистят projection по generation.
    // §9 demo-lobby: каждое подключение создаёт нового игрока, поэтому старые
    // userId, sessionId и баланс не переносятся в новое соединение.
    intentionalCloseRef.current = false;
    walletRequestedRef.current = false;
    useUserStore.getState().clearProfile();
    setRoomId(null);
    setChatMode(ChatModeEnum.CHAT_MODE_NONE);
    setMissedWindows(0);
    setGeneration(attemptGeneration);

    const isCurrent = () => generationRef.current === attemptGeneration;

    const openSocket = () => {
      // 7. Generation guard непосредственно перед WebSocket constructor.
      if (!isCurrent()) return;

      setStatus("connecting");
      const ws = new WebSocket(WS_URL);
      ws.binaryType = "arraybuffer";
      socketRef.current = ws;

      const handleOpen = () => {
        if (!isCurrent()) return;
        lastActivityAtRef.current = Date.now();
        autoAttemptsRef.current = 0;
        setStatus("open");
      };

      const handleMessage = async (ev: MessageEvent) => {
        if (!isCurrent()) return;
        lastActivityAtRef.current = Date.now();
        const decoded = await decodeGameMessage(ev.data);
        // Повторная проверка: decode асинхронный.
        if (!decoded || !isCurrent()) return;

        if (decoded.type === MessageType.CONNECTED && decoded.connected) {
          // 8. Room и capability читаются заново на каждом CONNECTED.
          const room = decoded.connected.roomId;
          const connectedRoomId = room == null ? null : Number(room);

          // §5 demo-lobby: несовпадение с выбором пользователя — ошибка
          // соединения. Такое соединение нельзя использовать для игры, и
          // автоматически переключать комнату нельзя.
          if (connectedRoomId !== expectedRoomId) {
            console.warn("ws: CONNECTED.roomId не совпал с выбранной комнатой");
            setStatus("error");
            intentionalCloseRef.current = true;
            ws.close(1000, "Room mismatch");
            return;
          }

          setRoomId(connectedRoomId);
          const mode = decoded.connected.chatCapabilities?.mode;
          // Absent capability и unknown mode означают fail closed.
          setChatMode(
            mode === ChatModeEnum.CHAT_MODE_VIEWER_AWARE ||
              mode === ChatModeEnum.CHAT_MODE_PUBLIC
              ? mode
              : ChatModeEnum.CHAT_MODE_NONE
          );
        }

        // §10 demo-lobby: начальный баланс сам не приходит — после первого
        // `GAME_STATE` соединения клиент обязан запросить кошелёк.
        if (
          decoded.type === MessageType.GAME_STATE &&
          !walletRequestedRef.current
        ) {
          walletRequestedRef.current = true;
          sendGetWallet(ws);
        }

        notifySubscribers(decoded);
      };

      const handleError = () => {
        if (!isCurrent()) return;
        setStatus("error");
      };

      const handleClose = () => {
        if (!isCurrent()) return;
        setStatus("closed");
        setChatMode(ChatModeEnum.CHAT_MODE_NONE);
        scheduleRetry();
      };

      ws.addEventListener("open", handleOpen);
      ws.addEventListener("message", handleMessage);
      ws.addEventListener("error", handleError);
      ws.addEventListener("close", handleClose);

      teardownRef.current = () => {
        ws.removeEventListener("open", handleOpen);
        ws.removeEventListener("message", handleMessage);
        ws.removeEventListener("error", handleError);
        ws.removeEventListener("close", handleClose);
        if (
          ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING
        ) {
          ws.close(1000, "Generation retired");
        }
        if (socketRef.current === ws) socketRef.current = null;
      };
    };

    // 5. Asynchronous backend-owned bootstrap с захваченной generation.
    if (!bootstrapBase) {
      openSocket();
      return;
    }

    setStatus("bootstrapping");
    fetch(`${bootstrapBase}/game/dev-session`, {
      method: "POST",
      credentials: "include",
      // Тело действительно пустое, Content-Type отсутствует, query отсутствует,
      // application-supplied credential/identity header отсутствует (§5.1).
    })
      .then((response) => {
        // 6. Guard после asynchronous completion. Callback retired generation
        // остаётся inert: не вызывает WebSocket constructor и не меняет state.
        if (!isCurrent()) return;
        if (response.status !== 204) {
          // Любой другой status — failed full-attempt admission.
          setStatus("error");
          scheduleRetry();
          return;
        }
        openSocket();
      })
      .catch(() => {
        // Origin rejection может быть CORS-masked как network failure.
        if (!isCurrent()) return;
        setStatus("error");
        scheduleRetry();
      });
  }, [
    WS_URL,
    selectedRoom,
    bootstrapBase,
    decodeGameMessage,
    notifySubscribers,
    scheduleRetry,
  ]);

  startAttemptRef.current = startAttempt;

  useEffect(() => {
    // Смена комнаты меняет WS URL — это новая full connection attempt (§11):
    // cleanup retire предыдущую generation, подписчики чистят projection.
    if (!WS_URL) {
      setStatus("idle");
      return;
    }

    // Через ref, чтобы effect не зависел от identity колбэка.
    startAttemptRef.current?.();
    return () => {
      intentionalCloseRef.current = true;
      generationRef.current += 1; // retire: поздние callbacks остаются inert
      teardownRef.current?.();
      teardownRef.current = null;
      if (retryTimerRef.current != null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      setStatus("closed");
    };
    // Пересоздаём attempt только при смене WS URL; startAttempt стабилен по ref.
  }, [WS_URL]);

  // Возврат сети запускает новую full attempt, а не request внутри old generation.
  useEffect(() => {
    const handleOnline = () => {
      if (socketRef.current?.readyState === WebSocket.OPEN) return;
      autoAttemptsRef.current = 0;
      startAttemptRef.current?.();
    };
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, []);

  const ping = useCallback((): boolean => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    try {
      const msg: com.achlid.crash.game.IGameMessage = {
        type: MessageType.PING,
        timestamp: Date.now(),
        ping: { clientTime: Date.now() },
      };
      const bin = com.achlid.crash.game.GameMessage.encode(msg).finish();
      socket.send(bin);
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (status !== "open") {
      setMissedWindows(0);
      return;
    }

    const intervalId = window.setInterval(() => {
      ping();

      const inactive = Date.now() - lastActivityAtRef.current >= 3000;
      setMissedWindows((prev) => (inactive ? Math.min(3, prev + 1) : 0));
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [status, ping]);

  /**
   * Методы намеренно стабильны на весь жизненный цикл провайдера: потребители
   * держат их в зависимостях эффектов (например `[status, sendProto]`), и
   * смена идентичности приводила бы к повторной отправке запросов и
   * `RATE_LIMIT` от бэкенда. Всё изменяемое состояние читается через ref.
   */
  const send = useCallback<WebSocketContextValue["send"]>((data) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(data);
    return true;
  }, []);

  const sendProto = useCallback<WebSocketContextValue["sendProto"]>((msg) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    try {
      const bin = com.achlid.crash.game.GameMessage.encode(msg).finish();
      socket.send(bin);
      return true;
    } catch {
      return false;
    }
  }, []);

  const close = useCallback<WebSocketContextValue["close"]>((code, reason) => {
    const socket = socketRef.current;
    if (!socket) return;
    // Намеренное закрытие: auto-reconnect не запускается.
    intentionalCloseRef.current = true;
    socket.close(code, reason);
  }, []);

  const reconnect = useCallback<WebSocketContextValue["reconnect"]>(() => {
    autoAttemptsRef.current = 0;
    startAttemptRef.current?.();
  }, []);

  const onMessage = useCallback<WebSocketContextValue["onMessage"]>(
    (type, handler) => {
      const subscribers = subscribersRef.current;
      let handlers = subscribers.get(type);
      if (!handlers) {
        handlers = new Set();
        subscribers.set(type, handlers);
      }
      handlers.add(handler);
      return () => {
        const current = subscribers.get(type);
        if (!current) return;
        current.delete(handler);
        if (current.size === 0) subscribers.delete(type);
      };
    },
    []
  );

  const value: WebSocketContextValue = useMemo(
    () => ({
      status,
      socket: socketRef.current,
      missedWindows,
      generation,
      roomId,
      chatMode,
      send,
      sendProto,
      onMessage,
      close,
      reconnect,
    }),
    [
      status,
      missedWindows,
      generation,
      roomId,
      chatMode,
      send,
      sendProto,
      onMessage,
      close,
      reconnect,
    ]
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}
